import { React, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import authApi from '../api/authApi';
import login from '../assets/login.jpg';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Invalid email format');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (!isLogin) {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
      if (!acceptedTerms) {
        setError('You must accept the terms & conditions');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (isLogin) {
        const response = await authApi.signIn({ email, password });
        
        if (response.data.list.length === 0) {
          throw new Error('User not found');
        }
        
        const user = response.data.list[0];
        localStorage.setItem('user', JSON.stringify(user));
        navigate('/dashboard');
      } else {
        await authApi.signUp({
          Email: email,
          Password: password,
        });
        
        const loginResponse = await authApi.signIn({ email, password });
        
        if (loginResponse.data.list.length > 0) {
          const user = loginResponse.data.list[0];
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        navigate('/dashboard');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.response?.data?.message || err.message || 'Authentication failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100vw', 
      display: 'flex', 
      overflow: 'hidden',
      bgcolor: 'background.default',
    }}>
      <Grid container>
        {/* Left Side - Image Section */}
        <Grid
          item
          md={6}
          xs={12}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'primary.dark',
              opacity: 0.9,
              zIndex: 1,
            }}
          />
          <img
            src={login}
            alt="Auth Background"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2,
              textAlign: 'center',
              color: 'common.white',
              width: '80%',
            }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'common.white',
              }}
            >
              Welcome to Orglance
            </Typography>
            <Typography 
              variant="h6"
              sx={{
                color: 'common.white',
              }}
            >
              Transform your organizational management with AI-powered solutions
            </Typography>
          </Box>
        </Grid>

        {/* Right Side - Form Section */}
        <Grid
          item
          md={6}
          xs={12}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="sm" sx={{ py: 6, px: { xs: 2, sm: 4 } }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 4,
                boxShadow: 1,
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Lock
                  sx={{
                    fontSize: 40,
                    color: 'primary.main',
                    mb: 2,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: 'text.primary',
                  }}
                >
                  {isLogin ? 'Welcome Back' : 'Create Account'}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {isLogin ? 'Sign in to continue' : 'Get started with your free account'}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email"
                  placeholder="orglance@gmail.com"
                  margin="normal"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  type="password"
                  label="Password"
                  placeholder="••••••••"
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />

                {!isLogin && (
                  <>
                    <TextField
                      fullWidth
                      type="password"
                      label="Confirm Password"
                      variant="outlined"
                      placeholder="••••••••"
                      margin="normal"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree to the{' '}
                          <Link
                            component={RouterLink}
                            to="/terms"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 500,
                            }}
                          >
                            Terms & Conditions
                          </Link>
                        </Typography>
                      }
                      sx={{ mb: 2 }}
                    />
                  </>
                )}

                {error && (
                  <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
                    {error}
                  </Typography>
                )}

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || !email || !password || (!isLogin && (!confirmPassword || !acceptedTerms))}
                  sx={{
                    mt: 2,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {isLogin ? 'New here? ' : 'Already have an account? '}
                  <Link
                    component="button"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                    onClick={() => setIsLogin(!isLogin)}
                  >
                    {isLogin ? 'Create an account' : 'Sign in instead'}
                  </Link>
                </Typography>

                {isLogin && (
                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 500,
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Typography>
                )}
              </Box>
            </Box>
          </Container>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AuthPage;